import React, { useEffect, useState } from 'react'
import AsyncSelect from '../../../components/AsyncSelect'
import { ConsolidateArgs, FetchItem, FetchOptions } from '../../../utils/appdata'
import deepEqual from 'fast-deep-equal'

const SelectQuery = ({
	id,
	name,
	label,
	helpText,
	value,
	onChange,
	query,
	optFields,
	context,
	getKeyOfOpt,
	optionKeyName,
	isInsideModal,
	showLabel,
}) => {
	const [options, setOptions] = useState([])
	const [cachedOptions, setCachedOptions] = useState()
	const [hasNextOptions, setHasNextOptions] = useState(true)
	const [isNextOptionsLoading, setIsNextOptionsLoading] = useState(false)
	const [search, setSearch] = useState('')
	const [args, setArgs] = useState(undefined)
  const globalContext = context.global

	const dataOption = (item) => {
		const c = globalContext.getPushedContext(item, query.typeName)
		return {
			value: getKeyOfOpt(item, optionKeyName),
			data: item,
			label: c.getSummary() || `(${query.typeName})`,
		}
	}

	const mergeOptions = () => {
		// merge cached (preset) options with those fetched from the query
		let keys = cachedOptions && Object.keys(cachedOptions)
		if (keys && keys.length) {
			const origKeyLen = keys.length
			if (options.length > 0) {
				// look for overlap / cached options no longer needed
				for (const opt of options) {
					if (opt.value in cachedOptions) {
						delete cachedOptions[opt.value]
						keys = Object.keys(cachedOptions)
						if (!keys || !keys.length) break
					}
				}
				if (keys.length !== origKeyLen) {
					setCachedOptions({ ...cachedOptions })
				}
			}
			if (keys && keys.length) {
				return keys.map(key => dataOption(cachedOptions[key])).concat(options)
			}
		}
		return options
	}

	useEffect(() => {
		if (value && !options.length) {
			const cache = {}
			if (Array.isArray(value)) {
				value.forEach(val => {
					cache[getKeyOfOpt(val, optionKeyName)] = val
				})
			} else {
				cache[getKeyOfOpt(value, optionKeyName)] = value
			}
			setCachedOptions(cache)
		} else {
			if (cachedOptions) {
				setCachedOptions(null)
			}
		}
	}, [value])

	useEffect(() => {
		const newArgs = query ? ConsolidateArgs(query, query.args.map(argStr => context.evaluateExpression(argStr))) : []
		if (!deepEqual(args, newArgs)) {
			setArgs(() => newArgs)
		}
	}, [args, query])

	const initOptions = async () => {
		setIsNextOptionsLoading(true)
		const fetchedOptions = await FetchOptions(query, optFields, args, globalContext, options.length, '')
		if (fetchedOptions) {
			setOptions(() => fetchedOptions.map(item => dataOption(item)))
			setHasNextOptions(fetchedOptions.length === 20)
		}
		setIsNextOptionsLoading(false)
	}

	useEffect(() => {
		if (args) {
			initOptions()
		}
	}, [args])

	const searchOptions = async (newSearch, updateOptions) => {
		setIsNextOptionsLoading(true)
		const newOptions = await FetchOptions(query, optFields, args, globalContext,
			newSearch === search ? options.length : 0, newSearch)
		const newMapped = newOptions.map(item => dataOption(item))
		setSearch(() => newSearch)
		updateOptions(newMapped)
		setOptions(() => newMapped)
		setIsNextOptionsLoading(false)
		setHasNextOptions(newMapped.length === 20)
	}

	const loadMoreOptions = isNextOptionsLoading ? () => {
	} : async () => {
		setIsNextOptionsLoading(true)
		const nextOptions = await FetchOptions(query, optFields, args, globalContext, options.length, search)
		setOptions((prevOptions) => {
			prevOptions.push(...nextOptions.map(item => dataOption(item)))
			return prevOptions
		})
		setIsNextOptionsLoading(false)
		setHasNextOptions(nextOptions.length === 20)
	}

	const isOptionsLoaded = index => !hasNextOptions || index < options.length

	const optionSelected = async (selected) => {
		if (!selected || !context.mutable) return
		const data = selected.data
		// async fetch entire selected option (rather than just minimal fields)
		let itemId = data && data[optionKeyName]
		if (itemId) {
			const varDef = context.getVarDef(name)
			const nextInst = context.instanceId + '.' + name + (varDef.isList ? '[]' : '')
			const nextValue = await FetchItem(query, itemId, optionKeyName, context, nextInst, true)
			if (nextValue) {
				onChange(nextValue)
			} else {
				console.error('Query returned invalid object after selection')
			}
		}
	}

	return (
		<AsyncSelect name={name}
		             label={label}
		             helpText={helpText}
		             id={id}
		             isInsideModal={isInsideModal}
		             value={getKeyOfOpt(value, optionKeyName)}
		             onChange={optionSelected}
		             options={mergeOptions()}
		             loadMoreOptions={loadMoreOptions}
		             isOptionsLoaded={isOptionsLoaded}
		             hasNextOptions={hasNextOptions}
		             loadOptions={searchOptions}
		             showLabel={showLabel}
		/>
	)
}

export default SelectQuery
